import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { hasPlus, useApp, useToast } from '../lib/store';
import { Link, navigate, useLocation } from '../lib/router';
import { useDesktop } from '../lib/useMedia';
import { Icon, type IconName } from './Icon';
import { Logo } from './Logo';

const TABS: { to: string; label: string; icon: IconName }[] = [
  { to: '/home', label: 'Home', icon: 'home' },
  { to: '/explore', label: 'Explore', icon: 'explore' },
  { to: '/reels', label: 'Reels', icon: 'reels' },
  { to: '/account', label: 'Account', icon: 'user' },
];

const SEEDED = ['n1', 'n2', 'n3', 'n4'];
function useUnread() {
  return useApp((s) => (!s.session ? 0 : s.notifIds
    ? s.notifIds.filter((id) => !s.readNotifs[id]).length
    : SEEDED.filter((id) => !s.readNotifs[id]).length + s.extraNotifs.filter((n) => !s.readNotifs[n.id]).length));
}

export function BottomNav() {
  const { path } = useLocation();
  return (
    <nav className="bottom-nav" aria-label="Main">
      {TABS.map((t) => {
        const on = path === t.to || path.startsWith(t.to + '/');
        return (
          <Link key={t.to} to={t.to} className={'bottom-tab' + (on ? ' on' : '')} aria-current={on ? 'page' : undefined}>
            <Icon name={t.icon} size={24} />
            <span>{t.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

export function TopNav() {
  const session = useApp((s) => s.session);
  const plus = useApp((s) => hasPlus(s));
  const unread = useUnread();
  const { path, query } = useLocation();
  const [q, setQ] = useState(query.get('q') || '');
  const links = [
    { to: '/home', label: 'Home' },
    { to: '/explore', label: 'Explore' },
    { to: '/reels', label: 'Reels' },
    { to: '/library', label: 'My library' },
  ];
  const submit = (e: FormEvent) => {
    e.preventDefault();
    navigate('/explore?q=' + encodeURIComponent(q.trim()));
  };
  return (
    <header className="top-nav">
      <div className="top-nav-inner">
        <Logo />
        <nav aria-label="Main" className="top-links">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className={'top-link' + (path.startsWith(l.to) ? ' on' : '')}>{l.label}</Link>
          ))}
        </nav>
        <form className="top-search" role="search" onSubmit={submit}>
          <label htmlFor="top-search" className="sr-only">Search shows</label>
          <Icon name="search" size={18} />
          <input id="top-search" type="search" placeholder="Search PAN card, SIP, interview…" value={q} onChange={(e) => setQ(e.target.value)} />
        </form>
        {session ? (
          <div className="top-actions">
            <Link to="/notifications" className="icon-btn" aria-label={unread ? `Notifications, ${unread} new` : 'Notifications'}>
              <Icon name="bell" />
              {unread > 0 && <span className="dot" />}
            </Link>
            {plus ? <span className="badge badge-plus">PLUS</span> : <Link to="/plus" className="btn btn-sm btn-primary">Get Plus</Link>}
            <Link to="/account" className="avatar" aria-label="Account">{session.name.slice(0, 1)}</Link>
          </div>
        ) : (
          <div className="top-actions">
            <Link to="/login" className="btn btn-sm btn-ghost">Log in</Link>
            <Link to="/login" className="btn btn-sm btn-primary">Start free</Link>
          </div>
        )}
      </div>
    </header>
  );
}

export function MobileHeader() {
  const unread = useUnread();
  const session = useApp((s) => s.session);
  return (
    <header className="m-header">
      <Logo size={30} />
      <div className="m-header-actions">
        <Link to="/explore" className="icon-btn" aria-label="Search"><Icon name="search" /></Link>
        {session ? (
          <Link to="/notifications" className="icon-btn" aria-label={unread ? `Notifications, ${unread} new` : 'Notifications'}>
            <Icon name="bell" />
            {unread > 0 && <span className="dot" />}
          </Link>
        ) : (
          <Link to="/login" className="btn btn-sm btn-primary">Log in</Link>
        )}
      </div>
    </header>
  );
}

export function PageHeader({ title, onBack, action }: { title: string; onBack?: () => void; action?: ReactNode }) {
  return (
    <header className="page-header">
      {onBack && (
        <button type="button" className="icon-btn" onClick={onBack} aria-label="Back"><Icon name="back" /></button>
      )}
      <h1>{title}</h1>
      {action}
    </header>
  );
}

/** Layout for the logged-in app: top nav on desktop, bottom tabs on phones. */
export function AppShell({ children, mobileHeader = true, bottomNav = true, flush = false }: {
  children: ReactNode; mobileHeader?: boolean; bottomNav?: boolean; flush?: boolean;
}) {
  const desktop = useDesktop();
  return (
    <div className={'app' + (desktop ? ' is-desktop' : ' is-phone')}>
      {desktop ? <TopNav /> : mobileHeader ? <MobileHeader /> : null}
      <main className={flush ? 'main flush' : 'main'}>{children}</main>
      {!desktop && bottomNav && <BottomNav />}
    </div>
  );
}

export function Toast() {
  const msg = useToast();
  // Keep the last message on screen for a short exit instead of vanishing.
  const [shown, setShown] = useState<string | null>(msg);
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (msg) { setShown(msg); setLeaving(false); return; }
    if (!shown) return;
    setLeaving(true);
    const t = window.setTimeout(() => { setShown(null); setLeaving(false); }, 150);
    return () => window.clearTimeout(t);
  }, [msg]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="toast-wrap" aria-live="polite">
      {shown && <div key={shown} className={'toast' + (leaving ? ' leaving' : '')} role="status">{shown}</div>}
    </div>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <span className="spinner" aria-hidden="true" />
      <span>{label}…</span>
    </div>
  );
}

/** Dark reel frame with a small spinner, used while a reel or episode is getting ready.
 *  Between reels there is never a full loading page: the screen stays black/poster-coloured. */
export function ReelBuffering({ color = '#1e1e20', title, onBack, desktop = false, slide = 'none', bottomNav = false }: {
  color?: string; title?: string; onBack?: () => void; desktop?: boolean; slide?: 'up' | 'down' | 'none'; bottomNav?: boolean;
}) {
  return (
    <AppShell mobileHeader={false} bottomNav={bottomNav} flush>
      <div className={'reels' + (bottomNav ? '' : ' watch-reel') + (desktop ? ' reels-desktop' : '')}>
        {onBack && (
          <div className="reels-top">
            <div className="watch-top-left">
              <button type="button" className="icon-btn glass" onClick={onBack} aria-label="Back"><Icon name="back" /></button>
              {title && <span className="watch-top-title"><b>{title}</b></span>}
            </div>
          </div>
        )}
        <div className={'reel-slide slide-' + slide}>
          <div className="player player-episode" style={{ '--poster': color } as CSSProperties}>
            <div className="player-stage">
              <div className="player-frame" />
              <span className="buffer-spinner" role="status" aria-label="Loading video" />
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

/** Grey 9:16 placeholders in the final layout while shows load, so nothing jumps when data arrives. */
export function SkeletonRails({ rails = 3, hero = false }: { rails?: number; hero?: boolean }) {
  return (
    <div className="skel" role="status" aria-label="Loading shows">
      {hero && <div className="skel-hero"><span className="skel-block skel-poster-lg" /><span className="skel-lines"><span className="skel-block skel-line w60" /><span className="skel-block skel-line w80" /><span className="skel-block skel-line w40" /></span></div>}
      {Array.from({ length: rails }, (_, r) => (
        <div key={r} className="skel-rail">
          <span className="skel-block skel-title" />
          <div className="skel-row">{Array.from({ length: 6 }, (_, i) => <span key={i} className="skel-block skel-poster" />)}</div>
        </div>
      ))}
    </div>
  );
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="error-box" role="alert">
      <p>{message}</p>
      {onRetry && <button type="button" className="btn btn-sm btn-ghost" onClick={onRetry}>Try again</button>}
    </div>
  );
}
